import {
    codeBlockLookBack,
    findCompleteCodeBlock,
    findPartialCodeBlock,
} from '@llm-ui/code';
import { markdownLookBack } from '@llm-ui/markdown';
import { throttleBasic, useLLMOutput, type LLMOutputComponent } from '@llm-ui/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import ShikiHighlighter from 'react-shiki';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { RenderBox } from '../../canvas/visualization/RenderHelpers';

const processOutput = (output: string) => {
    const match = output.match(/```(\w+)[\s\S]*```/);
    if (match) {
        const language = match[1];
        const code = output.match(/```\w+([\s\S]*)```/)?.[1]?.trim() || '';
        return [language, code];
    }
    return ['', output];
};

const htmlTemplate = (code: string) => `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
</head>
<body class="">
    ${code}
</body>
</html>
`;

const HtmlBlock: LLMOutputComponent = ({ blockMatch }) => {

    const [lang, code] = processOutput(blockMatch.output);

    return <iframe
        // ref={setIframe}
        title="Dynamic Content"
        style={{
            width: '100%',
            height: '100%',
        }}
        srcDoc={htmlTemplate(code)}
    />;
};

const ShikiBlock: LLMOutputComponent = ({ blockMatch }) => {
    const [language, code] = processOutput(blockMatch.output);
    return <ShikiHighlighter language={language} theme="github-light">
        {code.trim()}
    </ShikiHighlighter>;
};

const Article = styled.div`
  /* Base typography */
  font-size: 1.125rem;
  line-height: 1.7;
  color: #333;
  margin: 0 auto;

  /* Headings */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin-top: 2.5rem;
    margin-bottom: 1.5rem;
    line-height: 1.3;
    color: #111;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  & h1 {
    font-size: 2.5rem;
    margin-top: 0;
  }

  & h2 {
    font-size: 2rem;
  }

  & h3 {
    font-size: 1.75rem;
  }

  & h4 {
    font-size: 1.5rem;
  }

  & h5 {
    font-size: 1.25rem;
  }

  & h6 {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Paragraphs and spacing */
  & p {
    margin-top: 0;
    margin-bottom: 1.5rem;
  }

  & p + p {
    margin-top: 1.5rem;
  }

  /* Links */
  & a {
    color: var(--color-blue-200);
    text-decoration: underline;
    text-decoration-thickness: 0.05em;
    text-underline-offset: 0.15em;
    transition: color 0.2s ease;
  }

  & a:hover {
    color: var(--color-blue-300);
  }

  & a:focus {
    outline: 2px solid var(--color-blue-200);
    outline-offset: 2px;
  }

  /* Lists */
  & ul,
  & ol {
    margin-top: 1rem;
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }

  & li {
    margin-bottom: 0.5rem;
  }

  & li > ul,
  & li > ol {
    margin-top: 0.5rem;
    margin-bottom: 0;
  }

  /* List markers */
  & ul {
    list-style-type: disc;
  }

  & ul ul {
    list-style-type: circle;
  }

  & ul ul ul {
    list-style-type: square;
  }

  & ol {
    list-style-type: decimal;
  }

  & ol ol {
    list-style-type: lower-alpha;
  }

  & ol ol ol {
    list-style-type: lower-roman;
  }

  /* Images */
  & img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 2rem 0;
  }

  /* Figures */
  & figure {
    margin: 2rem 0;
    display: block;
  }

  & figcaption {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #666;
    text-align: center;
    font-style: italic;
  }

  /* Blockquotes */
  & blockquote {
    margin: 2rem 0;
    padding: 1rem 0 1rem 2rem;
    border-left: 4px solid #e0e0e0;
    font-style: italic;
    color: #555;
  }

  & blockquote p:last-child {
    margin-bottom: 0;
  }

  /* Code */
  & code {
    font-family:
      "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;
    font-size: 0.9em;
    background: rgba(0, 0, 0, 0.05);
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }

  & pre {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 1rem;
    overflow-x: auto;
    margin: 1.5rem 0;
  }

  & pre code {
    background: transparent;
    padding: 0;
    font-size: 0.85em;
    line-height: 1.6;
  }

  /* Horizontal rule */
  & hr {
    height: 2px;
    background-color: #e0e0e0;
    border: none;
    margin: 3rem 0;
  }

  /* Tables */
  & table {
    width: 100%;
    border-collapse: collapse;
    margin: 2rem 0;
    font-size: 0.95em;
  }

  & th,
  & td {
    padding: 0.75rem;
    border: 1px solid #e0e0e0;
  }

  & th {
    background-color: #f5f5f5;
    font-weight: 600;
    text-align: left;
  }

  & tbody tr:nth-child(odd) {
    background-color: #f9f9f9;
  }

  /* Abbreviations */
  & abbr[title] {
    text-decoration: underline dotted;
    cursor: help;
  }

  /* Small text */
  & small {
    font-size: 0.85em;
  }

  /* Responsive tweaks */
  @media (max-width: 768px) {
    font-size: 1rem;

    & h1 {
      font-size: 2rem;
    }

    & h2 {
      font-size: 1.75rem;
    }

    & h3 {
      font-size: 1.5rem;
    }

    & h4 {
      font-size: 1.25rem;
    }

    & blockquote {
      padding-left: 1rem;
    }
  }
`;

// Customize this component with your own styling
const CodeSelectorBlock: LLMOutputComponent = ({ blockMatch }) => {
    // const [language] = processOutput(blockMatch.output);
    // if (language === 'html') return <HtmlBlock blockMatch={blockMatch} />;
    return <ShikiBlock blockMatch={blockMatch} />;
};

// Customize this component with your own styling
const MarkdownBlock: LLMOutputComponent = ({ blockMatch }) => {
    const markdown = blockMatch.output;
    return <Article>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </Article>;
};

const throttle = throttleBasic({
    readAheadChars: 10,
    targetBufferChars: 7,
    adjustPercentage: 0.35,
    frameLookBackMs: 10000,
    windowLookBackMs: 2000,
});

export const RenderFencedContent = React.memo<{ content: string, complete: boolean }>(({ content, complete }) => {

    const { blockMatches } = useLLMOutput({
        llmOutput: content,
        fallbackBlock: {
            component: MarkdownBlock, // from Step 1
            lookBack: markdownLookBack(),
        },
        blocks: [
            {
                component: CodeSelectorBlock, // from Step 2
                findCompleteMatch: findCompleteCodeBlock(),
                findPartialMatch: findPartialCodeBlock(),
                lookBack: codeBlockLookBack(),
            },
        ],
        isStreamFinished: complete,
        throttle,
    });

    return <RenderBox inset="0" overflow="scroll">
        {blockMatches.map((blockMatch, index) => {
            const Component = blockMatch.block.component;
            return <Component key={index} blockMatch={blockMatch} />;
        })}
    </RenderBox>;
});
